import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2Icon,
  MicIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  SendHorizonalIcon,
  XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import TooltipComponent from '@/components/common/tooltip/tooltip';

const RECORDING_STATES = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PROCESSING: 'processing',
  REVIEW: 'review',
};

const MIN_RECORDING_DURATION = 2000; // 2 seconds in milliseconds
const MAX_RECORDING_DURATION = 180000; // 3 minutes in milliseconds

export default function AudioRecorder({
  onRecordingComplete,
  onCancel,
  isRecording,
  setIsRecording,
  setIsAudioRecorderActive,
}) {
  const [recordingState, setRecordingState] = useState(RECORDING_STATES.IDLE);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRef = useRef(null);

  // Notify parent when recording state changes
  useEffect(() => {
    const isActive = recordingState !== RECORDING_STATES.IDLE;
    setIsAudioRecorderActive?.(isActive);
  }, [recordingState, setIsAudioRecorderActive]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  const resetToIdle = useCallback(() => {
    cleanup();
    setRecordingState(RECORDING_STATES.IDLE);
    setIsRecording(false);
    setRecordingDuration(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
  }, [cleanup, setIsRecording]);

  // Format time for display
  const formatTime = useCallback((milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // Check minimum duration
      if (recordingDuration < MIN_RECORDING_DURATION) {
        toast.error('Recording too short', {
          description: 'Please record at least 2 seconds of audio',
        });
        resetToIdle();
        return;
      }
    }
  }, [recordingDuration, resetToIdle]);

  // A ref to hold the latest stopRecording function
  const stopRecordingRef = useRef(stopRecording);
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        toast.error('Audio recording not supported', {
          description:
            'Your browser does not support audio recording. Please use a modern browser.',
        });
        setRecordingState(RECORDING_STATES.IDLE);
        return;
      }

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      // Set up MediaRecorder
      let mediaRecorder;
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/wav',
      ];

      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported audio format found');
      }

      console.log('Using audio format:', selectedMimeType);

      mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
      });

      mediaRecorderRef.current = mediaRecorder;

      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: selectedMimeType });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setRecordingState(RECORDING_STATES.REVIEW);
        setIsRecording(false);
      };

      // Start recording
      mediaRecorder.start();
      setRecordingState(RECORDING_STATES.RECORDING);
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      const startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        setRecordingDuration(elapsed);

        // Auto-stop at max duration
        if (elapsed >= MAX_RECORDING_DURATION) {
          stopRecordingRef.current();
        }
      }, 100);
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone permission denied', {
          description:
            'Please allow microphone access to record audio messages',
        });
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found', {
          description: 'Please connect a microphone to record audio messages',
        });
      } else {
        toast.error('Failed to start recording', {
          description: 'Please try again or check your microphone settings',
        });
      }
      resetToIdle();
    }
  }, [setIsRecording, resetToIdle]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.stop();
    }
    resetToIdle();
    onCancel?.();
  }, [onCancel, resetToIdle]);

  // Retry recording
  const retryRecording = useCallback(() => {
    cleanup();
    setRecordingDuration(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setIsPlaying(false);
    startRecording();
  }, [cleanup, startRecording]);

  // Send recording
  const sendRecording = useCallback(async () => {
    if (audioBlob) {
      setRecordingState(RECORDING_STATES.PROCESSING);

      try {
        // Create a new blob with proper filename
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, '-')
          .slice(0, -5);

        // Extract file extension properly, removing codecs information
        let fileExtension = 'webm'; // default
        if (audioBlob.type) {
          const mimeType = audioBlob.type.split(';')[0]; // Remove codecs part
          const parts = mimeType.split('/');
          if (parts.length > 1) {
            fileExtension = parts[1];
          }
        }

        const filename = `audio-recording-${timestamp}.${fileExtension}`;

        // Create a new File object with the proper name
        const fileWithName = new File([audioBlob], filename, {
          type: audioBlob.type,
        });

        await onRecordingComplete(fileWithName);

        // After successful send, reset to idle state
        resetToIdle();
      } catch (error) {
        console.error('Error sending audio:', error);
        toast.error('Failed to send audio recording');
        setRecordingState(RECORDING_STATES.REVIEW);
      }
    }
  }, [audioBlob, onRecordingComplete, resetToIdle]);

  // Play/pause preview
  const togglePreview = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Handle external recording state changes
  useEffect(() => {
    if (isRecording && recordingState === RECORDING_STATES.IDLE) {
      startRecording();
    }
  }, [isRecording, recordingState, startRecording]);

  // Render recording interface - now takes over the entire input area
  if (recordingState === RECORDING_STATES.RECORDING) {
    return (
      <div className='flex w-full items-center justify-between rounded-lg bg-destructive p-3 text-destructive-foreground animate-in fade-in zoom-in-95'>
        <div className='flex flex-1 items-center gap-2'>
          <div className='relative'>
            <MicIcon className='h-5 w-5 animate-pulse' />
            <div className='absolute inset-0 animate-ping rounded-full bg-destructive-foreground opacity-20' />
          </div>
          <span className='text-sm font-medium'>
            Recording... {formatTime(recordingDuration)}
          </span>
        </div>

        <div className='ml-auto flex items-center gap-2'>
          <Button
            size='sm'
            onClick={stopRecording}
            className='border border-destructive-foreground/50 bg-transparent text-destructive-foreground shadow-sm hover:bg-destructive-foreground hover:text-destructive'
          >
            Stop
          </Button>
          <Button
            size='sm'
            variant='ghost'
            onClick={cancelRecording}
            className='text-muted-foreground hover:text-foreground'
          >
            <XIcon className='h-4 w-4' />
          </Button>
        </div>
      </div>
    );
  }

  // Render review interface - now takes over the entire input area
  if (recordingState === RECORDING_STATES.REVIEW) {
    return (
      <div className='flex w-full items-center justify-between rounded-lg border bg-secondary p-3 animate-in fade-in zoom-in-95'>
        <div className='flex items-center gap-2'>
          <Button
            size='sm'
            variant='ghost'
            onClick={togglePreview}
            className='text-primary hover:bg-primary/10'
          >
            {isPlaying ? (
              <PauseIcon className='h-4 w-4' />
            ) : (
              <PlayIcon className='h-4 w-4' />
            )}
          </Button>
          <span className='text-sm font-medium text-primary'>
            {formatTime(recordingDuration)}
          </span>
          <Button
            size='icon'
            variant='ghost'
            onClick={retryRecording}
            className='h-8 w-8 text-muted-foreground hover:text-foreground'
            title='Retry recording'
          >
            <RotateCcwIcon className='h-4 w-4' />
          </Button>
          <Button
            size='icon'
            variant='ghost'
            onClick={cancelRecording}
            className='h-8 w-8 text-muted-foreground hover:text-foreground'
            title='Discard audio'
          >
            <XIcon className='h-4 w-4' />
          </Button>
        </div>

        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          className='hidden'
        />

        <Button size='sm' variant='default' onClick={sendRecording}>
          <SendHorizonalIcon className='mr-1 h-4 w-4' />
          Send
        </Button>
      </div>
    );
  }

  // Render processing state - now takes over the entire input area
  if (recordingState === RECORDING_STATES.PROCESSING) {
    return (
      <div className='flex w-full items-center justify-center rounded-lg border bg-muted p-3 animate-in fade-in zoom-in-95'>
        <div className='flex items-center gap-3'>
          <Loader2Icon className='h-5 w-5 animate-spin text-muted-foreground' />
          <span className='text-sm font-medium text-muted-foreground'>
            Sending audio...
          </span>
        </div>
      </div>
    );
  }

  // Render idle state (mic button)
  return (
    <TooltipComponent
      content={
        <div className='flex space-x-2'>
          <div>Record audio message</div>
          <div className='flex space-x-1'>
            <kbd className='pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
              {navigator.platform.toLowerCase().includes('mac')
                ? 'Option'
                : 'Alt'}
            </kbd>
            <kbd className='pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex'>
              R
            </kbd>
          </div>
        </div>
      }
    >
      <Button
        size='xs'
        variant='ghost'
        onClick={startRecording}
        className='text-primary transition-all hover:text-primary/90'
      >
        <MicIcon className='h-5 w-5' />
      </Button>
    </TooltipComponent>
  );
}
